import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

function DataConsistencyCheck() {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState(null);

  const getSkuLocationSummary = (item) => {
    if (!item.skuLocations || item.skuLocations.length === 0) {
      return item.location || 'N/A';
    }

    const grouped = {};
    item.skuLocations.forEach(sku => {
      const loc = sku.location || item.location || 'N/A';
      if (!grouped[loc]) {
        grouped[loc] = [];
      }
      grouped[loc].push(sku);
    });

    const entries = Object.entries(grouped);
    if (entries.length === 0) return item.location || 'N/A';

    const actualSkuCount = item.skuLocations.length;
    const isBulkProduct = actualSkuCount > 1;

    if (isBulkProduct) {
      return entries.map(([loc, items]) => `${loc} (${items.length}개)`).join(', ');
    }

    if (entries.length === 1) {
      return entries[0][0];
    }

    return entries.map(([loc, items]) => `${loc} (${items.length}개)`).join(', ');
  };

  const checkConsistency = async () => {
    setLoading(true);
    setIssues([]);
    setSummary(null);

    try {
      const snapshot = await getDocs(collection(db, 'inventory'));
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const foundIssues = [];
      const debugInfo = [];

      items.forEach(item => {
        const itemIssues = [];
        const quantity = Number(item.quantity) || 1;
        const skuCount = item.skuLocations?.length || 0;

        debugInfo.push({
          id: item.id,
          name: item.productName,
          quantity,
          quantityRaw: item.quantity,
          skuCount,
          productType: item.productType
        });

        // 1. location과 skuLocations 불일치 확인
        if (item.skuLocations && item.skuLocations.length > 0) {
          const skuSummary = getSkuLocationSummary(item);
          const mainLocation = item.location;

          const allSkusInSameLocation = item.skuLocations.every(
            sku => sku.location === mainLocation
          );

          if (!allSkusInSameLocation) {
            itemIssues.push({
              type: 'LOCATION_MISMATCH',
              description: `location 필드(${mainLocation})와 skuLocations가 불일치`,
              details: `SKU 위치: ${skuSummary}`
            });
          }
        }

        // 2. productType과 quantity 불일치 확인
        if (item.productType === 'unique' && quantity > 1) {
          itemIssues.push({
            type: 'TYPE_QUANTITY_MISMATCH',
            description: `일품 제품인데 수량이 ${quantity}개`,
            details: `productType: unique, quantity: ${quantity}`
          });
        }

        if (item.productType === 'unique' && skuCount > 1) {
          itemIssues.push({
            type: 'TYPE_SKU_MISMATCH',
            description: `일품 제품인데 SKU가 ${skuCount}개`,
            details: `productType: unique, skuLocations: ${skuCount}개`
          });
        }

        if (item.productType === 'bulk' && quantity === 1) {
          itemIssues.push({
            type: 'BULK_SINGLE_QUANTITY',
            description: `수량관리 제품인데 수량이 1개`,
            details: `productType: bulk, quantity: 1`
          });
        }

        // 3. quantity와 skuLocations 개수 불일치
        if (skuCount > 0 && quantity !== skuCount) {
          itemIssues.push({
            type: 'QUANTITY_SKU_COUNT_MISMATCH',
            description: `수량(${quantity})과 SKU 개수(${skuCount})가 불일치`,
            details: `quantity: ${quantity}, skuLocations: ${skuCount}개`
          });
        }

        if (itemIssues.length > 0) {
          foundIssues.push({
            id: item.id,
            productName: item.productName,
            barcodeId: item.barcodeId,
            location: item.location,
            quantity,
            productType: item.productType,
            skuCount,
            issues: itemIssues
          });
        }
      });

      console.log('=== 전체 제품 정보 ===');
      console.log('총 제품 수:', items.length);
      console.log('Cabinet 찾기:', debugInfo.filter(d => d.name?.includes('Cabinet')));
      console.log('수량과 SKU 불일치:', debugInfo.filter(d => d.quantity !== d.skuCount && d.skuCount > 0));
      console.log('전체 데이터:', debugInfo);

      setIssues(foundIssues);
      setSummary({
        total: items.length,
        issueCount: foundIssues.length
      });

    } catch (error) {
      console.error('검사 중 오류:', error);
      alert(`오류 발생: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    const csvContent = [
      'ID,제품명,바코드,위치,수량,제품유형,SKU개수,문제유형,문제설명,상세정보',
      ...issues.flatMap(item =>
        item.issues.map(issue =>
          [
            item.id,
            item.productName,
            item.barcodeId,
            item.location,
            item.quantity,
            item.productType,
            item.skuCount,
            issue.type,
            issue.description,
            issue.details
          ].map(v => `"${v}"`).join(',')
        )
      )
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `data-consistency-issues-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const issuesByType = {};
  issues.forEach(item => {
    item.issues.forEach(issue => {
      if (!issuesByType[issue.type]) {
        issuesByType[issue.type] = [];
      }
      issuesByType[issue.type].push({ ...item, issueDetail: issue });
    });
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-4 border-b-2 border-green-500 pb-2">
          📊 재고 데이터 일관성 검사
        </h1>

        <p className="mb-6 text-gray-600">
          이 도구는 전체 재고 데이터를 검사하여 location과 skuLocations 간의 불일치를 찾아냅니다.
        </p>

        <div className="flex gap-4 mb-6">
          <button
            onClick={checkConsistency}
            disabled={loading}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg disabled:bg-gray-400"
          >
            {loading ? '검사 중...' : '검사 시작'}
          </button>

          {issues.length > 0 && (
            <button
              onClick={downloadCSV}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
            >
              CSV 다운로드
            </button>
          )}
        </div>

        {summary && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <h2 className="font-bold text-lg mb-2">검사 완료</h2>
            <p>
              총 <strong>{summary.total}</strong>개 중{' '}
              <strong className="text-red-600">{summary.issueCount}</strong>개 제품에서 문제 발견
            </p>
          </div>
        )}

        {summary && issues.length === 0 && (
          <div className="text-green-600 font-bold text-lg">
            ✅ 모든 데이터가 일관성 있게 저장되어 있습니다!
          </div>
        )}

        {Object.entries(issuesByType).map(([type, items]) => (
          <div key={type} className="mb-8">
            <h3 className="text-xl font-bold text-red-600 mb-4">
              ❌ {type}: {items.length}개
            </h3>

            {items.map((item, index) => (
              <div key={item.id} className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <div className="font-bold mb-2">
                  [{index + 1}] {item.productName} ({item.barcodeId})
                </div>
                <div className="text-sm space-y-1">
                  <div>
                    ID:{' '}
                    <a
                      href={`/inventory/${item.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {item.id}
                    </a>
                  </div>
                  <div>위치: {item.location}</div>
                  <div>수량: {item.quantity}</div>
                  <div>제품유형: {item.productType}</div>
                  <div>SKU개수: {item.skuCount}</div>
                  <div className="text-red-600 font-semibold">
                    문제: {item.issueDetail.description}
                  </div>
                  <div>상세: {item.issueDetail.details}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default DataConsistencyCheck;
